import React, { useState, useRef, useEffect } from "react";
import {
    Typography, Button, CircularProgress, Snackbar, Alert, Box,
    Collapse, List, ListItem, ListItemText, Chip, MenuItem, Select,
    FormControl, InputLabel
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "axios";
import { useSelector } from "react-redux";
import { apiHost } from '../utils/apiHost';

const getApiBaseUrl = () => {
    const protocol = window.location.protocol === "https:" ? "https" : "http";
    const baseUrl = apiHost().replace(/^https?:\/\/+/, "");
    return `${protocol}://${baseUrl}`;
};

export const UploadProduk = ({ inline = false, onSuccess }) => {
    const { user } = useSelector((state) => state.auth);
    const isSuperadmin = user?.role === "superadmin";

    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [showErrors, setShowErrors] = useState(false);
    const [cabangList, setCabangList] = useState([]);
    const [selectedCabang, setSelectedCabang] = useState("");
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!isSuperadmin) return;
        axios
            .get(`${getApiBaseUrl()}/cabangbyrole`, { withCredentials: true })
            .then((res) => {
                const list = res.data?.data;
                if (Array.isArray(list)) setCabangList(list);
            })
            .catch(() => {});
    }, [isSuperadmin]);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setUploadResult(null);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setSnackbar({ open: true, message: "Silakan pilih file untuk diunggah.", severity: "warning" });
            return;
        }
        if (isSuperadmin && !selectedCabang) {
            setSnackbar({ open: true, message: "Silakan pilih cabang terlebih dahulu.", severity: "warning" });
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        if (isSuperadmin && selectedCabang) {
            formData.append("cabanguuid", selectedCabang);
        }

        try {
            setLoading(true);
            setUploadResult(null);
            const response = await axios.post(`${getApiBaseUrl()}/upload`, formData, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });

            setLoading(false);
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setUploadResult(response.data);
            setSnackbar({ open: true, message: response.data.message || "File berhasil diunggah.", severity: "success" });
            if (onSuccess) onSuccess();
        } catch (error) {
            setLoading(false);
            const msg = error.response?.data?.message || error.response?.data?.msg || "Terjadi kesalahan saat mengunggah file.";
            setSnackbar({ open: true, message: msg, severity: "error" });
        }
    };

    const hint = (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 440, lineHeight: 1.5 }}>
            Kolom wajib: <strong>namabarang</strong>, <strong>namakategori</strong>, <strong>harga</strong>.
            Opsional: <strong>harga_modal</strong>, <strong>kodebarang</strong>, <strong>stokCabang</strong>, <strong>foto</strong>.
            Gunakan template yang disediakan.
        </Typography>
    );

    const cabangSelector = isSuperadmin && (
        <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Pilih Cabang</InputLabel>
            <Select
                value={selectedCabang}
                onChange={(e) => setSelectedCabang(e.target.value)}
                label="Pilih Cabang"
                disabled={loading}
            >
                {cabangList.map((c) => (
                    <MenuItem key={c.uuid} value={c.uuid}>
                        {c.namacabang}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const controls = (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            {cabangSelector}
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="upload-produk-input"
            />
            <label htmlFor="upload-produk-input">
                <Button variant="outlined" component="span" size="small" disabled={loading}>
                    {selectedFile
                        ? selectedFile.name.slice(0, 26) + (selectedFile.name.length > 26 ? "…" : "")
                        : "Pilih File"}
                </Button>
            </label>
            <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleUpload}
                disabled={loading || !selectedFile || (isSuperadmin && !selectedCabang)}
            >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Upload"}
            </Button>
        </Box>
    );

    const resultSummary = uploadResult?.summary && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: uploadResult.summary.errors?.length > 0 ? 1 : 0 }}>
                <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label={`${uploadResult.summary.insertedBarang} produk baru`}
                    size="small"
                    color="success"
                    variant="outlined"
                />
                {uploadResult.summary.updatedBarang > 0 && (
                    <Chip
                        label={`${uploadResult.summary.updatedBarang} produk diperbarui`}
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                )}
                <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label={`${uploadResult.summary.insertedBarangCabang} cabang baru`}
                    size="small"
                    color="info"
                    variant="outlined"
                />
                <Chip
                    label={`${uploadResult.summary.updatedBarangCabang} stok diperbarui`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
                {uploadResult.summary.skipped > 0 && (
                    <Chip
                        icon={<ErrorOutlineIcon />}
                        label={`${uploadResult.summary.skipped} baris dilewati`}
                        size="small"
                        color="warning"
                        variant="outlined"
                    />
                )}
            </Box>

            {uploadResult.summary.errors?.length > 0 && (
                <>
                    <Button
                        size="small"
                        onClick={() => setShowErrors((v) => !v)}
                        endIcon={showErrors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ textTransform: "none", p: 0, minWidth: 0, fontSize: 12 }}
                        color="warning"
                    >
                        {showErrors ? "Sembunyikan" : "Lihat"} detail baris yang dilewati ({uploadResult.summary.errors.length})
                    </Button>
                    <Collapse in={showErrors}>
                        <List dense disablePadding sx={{ mt: 0.5, maxHeight: 150, overflowY: "auto" }}>
                            {uploadResult.summary.errors.map((err, i) => (
                                <ListItem key={i} disableGutters sx={{ py: 0 }}>
                                    <ListItemText
                                        primary={err}
                                        primaryTypographyProps={{ variant: "caption", color: "warning.dark" }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </>
            )}
        </Box>
    );

    if (inline) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, flex: "1 1 200px", minWidth: 0 }}>
                {hint}
                {controls}
                {resultSummary}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={5000}
                    onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            {hint}
            <Box sx={{ mt: 1 }}>{controls}</Box>
            {resultSummary}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};